import { WidthLimiter, Wrapper } from 'components/navigation/header/styled';
import React, { useRef, useState } from 'react';
import * as Styled from './styled';
import ArrowIcon from 'assets/svg/left.svg';
import BackLink from 'components/navigation/BackLink';
import { ShareButtonLocal } from 'components/navigation/share/ShareButton';
import ShareIcon from 'assets/svg/big-share-ic.svg';
import { Info } from '../common/Info';
import { useIntersectionObserver } from 'hooks/useIntersectionObserver';
import { headerHeightConsts } from 'ds/theme/default';
import { NavigationWrapper } from './styled';
import { Navigation } from '../common/Navigation';
import { HeaderWrapper } from 'ds/components/Layout';
import { IOfficeHeaderInfo, PageDetails } from 'screens/AgentOfficePages/types';
import { Route, useRoute } from 'config/routes';
import { useShareHandler } from '../common/useShareHandler';
import withEvents from 'analytics/withEvents';
import { noop } from 'lodash';
import { ShareType } from 'helpers/share-helpers';
import { shareMethodToEvent } from 'screens/AgentOfficePages/common/components/Social';


const intersectionOptions = {
  threshold: [ 0, 1 ],
  rootMargin: `-${headerHeightConsts.mobile.menu}px 0px 0px 0px`,
};

const MobileHeaderBase: React.FC<IOfficeHeaderInfo> = (props) => {
  const {
    title,
    logo,
    phone,
    address,
    sections,
    activeSection,
    officeName,
    officeLogo,
    agentSubtitle,
    officeId,
    onShareClick = noop,
    onShareOptionClick = noop,
    pageDetails,
    onPhoneExpose = noop,
    isLeadingAgent,
  } = props;
  const infoNode = useRef<HTMLDivElement>(null);
  const [ infoVisible, setInfoVisible ] = useState(false);
  const { route } = useRoute();
  const shareHandler = useShareHandler(title);
  const isAgent = route.name === Route.AgentPage;
  const event = isAgent ? 'agent_share_click' : 'office_share_click';
  const eventMethod = isAgent ? 'agent_share_method' : 'office_share_method';
  const category = isAgent ? 'agent' : 'office';
  const eventShareHandler = () => {
    onShareClick(event, category, 'sticky', pageDetails);
  };

  const handleShareMethod = (method: ShareType) => {
    onShareOptionClick(eventMethod, category, 'sticky', pageDetails, shareMethodToEvent[method]);
    shareHandler(method);
  };

  const handleStatisPhoneClickEvent = () => {
    onPhoneExpose(isAgent ? 'agent_page' : 'office_page', props);
  };

  useIntersectionObserver(([ entry ]) => {
    setInfoVisible(!entry.isIntersecting);
  }, [ infoNode ], intersectionOptions);

  return (
    <>
      <HeaderWrapper>
        <WidthLimiter>
          <Wrapper className="mobile-header-wrapper" small>
            <Styled.Inner>
              <Styled.BackButtonWrapper data-auto="back-button">
                <BackLink>{<ArrowIcon width={24} height={24} />}</BackLink>
              </Styled.BackButtonWrapper>
              <Styled.Title show={infoVisible}>
                <Styled.Name weight="medium">{title}{isAgent ? <span> · {officeName}</span> : null}</Styled.Name>
              </Styled.Title>
              <ShareButtonLocal
                shouldStayOpen={() => false}
                shouldResetSelectedValue={() => false}
                handleShare={handleShareMethod}
              >
                <div onClick={eventShareHandler}>
                  <ShareIcon />
                </div>
              </ShareButtonLocal>
            </Styled.Inner>
          </Wrapper>
        </WidthLimiter>
      </HeaderWrapper>
      <Styled.InfoWrapper>
        {isAgent ? (
          <Styled.OfficeInfo routeName={Route.OfficePage} routeParams={{ id: officeId, source: 'agent_page' }} hideLinkParams>
            <Styled.OfficeLogo><img src={officeLogo} width={42} height={42} /></Styled.OfficeLogo>
            <Styled.OfficeName weight="bold">{officeName}</Styled.OfficeName>
          </Styled.OfficeInfo>
        ) : null}
        <Info {...{ title, logo, phone, about: address || agentSubtitle, isLeadingAgent }} onPhoneClickEventHandler={handleStatisPhoneClickEvent} />
        <div ref={infoNode} />
      </Styled.InfoWrapper>
      {isAgent ? null : (
        <NavigationWrapper>
          <Navigation sections={sections} activeSection={activeSection} pageDetails={pageDetails} />
        </NavigationWrapper>
      )}
    </>
  );
};

export const MobileHeader = withEvents<IOfficeHeaderInfo>((sendEvent) => ({
  onShareClick: (event: string, category: string, source: string, pageDetails: PageDetails) => {
    sendEvent(event, category, {
      ...pageDetails,
      event: {
        source,
      },
    });
  },
  onShareOptionClick: (event: string, category: string, source: string, pageDetails: PageDetails, method: string) => {
    sendEvent(event, category, {
      ...pageDetails,
      event: {
        source,
        share_method: method,
      },
    });
  },
  onPhoneExpose: (source: string, props: IOfficeHeaderInfo) => {
    sendEvent('user_contact_me_call_submit', 'user', {
      ...('agent_page' === source ? {
        property: {
          property_id: 'property_id',
          deal_type: 'for_sale',
          bulletin_info: {
            agent_details: {
              id: props.pageDetails.agent_details.id,
              name: props.pageDetails.agent_details.name,
              office_id: props.pageDetails.agent_details.office_id,
              office_name: props.pageDetails.office_details.name,
              phone_number: props.phone,
              is_leading_agent: props.isLeadingAgent,
            },
          },
        },
      } : {}),
      event: {
        source,
      },
      rtbData: {
        phone: props.phone,
      },
    });
  },
}))(MobileHeaderBase);
